import { atom } from 'jotai'
import {
  add,
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  format,
  startOfMonth,
  endOfMonth,
  sub
} from '@sequencehq/utils/dates'
import {
  DAY_RESOLUTION_PX,
  TIMELINE_BUFFER_IN_YEARS,
  TIMELINE_DATE_OFFSET_FORMAT,
  TIMELINE_DATE_PHASE_FORMAT
} from '../timeline.constants'
import { productPriceHistoryAtom } from './productPriceHistory.atom'
import { processedPhasesAtom } from './processedPhases.atom'
import { minimumsHistoryAtom } from './minimumsHistory.atom'
import { ProcessedPhase } from './scheduleData.atom'

export const timelineDatesAtom = atom<{
  timelineStartDate: Date
  timelineEndDate: Date
}>(get => {
  const phases = get(processedPhasesAtom)

  if (!phases.length) {
    return {
      timelineStartDate: startOfMonth(
        sub(new Date(), { years: TIMELINE_BUFFER_IN_YEARS })
      ),
      timelineEndDate: endOfMonth(
        add(new Date(), { years: TIMELINE_BUFFER_IN_YEARS })
      )
    }
  }

  return {
    timelineStartDate: startOfMonth(
      sub(phases[0].startDate, { years: TIMELINE_BUFFER_IN_YEARS })
    ),
    timelineEndDate: endOfMonth(
      add(phases[phases.length - 1].endDate, {
        years: TIMELINE_BUFFER_IN_YEARS
      })
    )
  }
})

export const allDaysInTimelineAtom = atom<Date[]>(get => {
  const { timelineStartDate, timelineEndDate } = get(timelineDatesAtom)

  return eachDayOfInterval({
    start: timelineStartDate,
    end: timelineEndDate
  })
})

const allMonthsInTimelineAtom = atom<Date[]>(get => {
  const { timelineStartDate, timelineEndDate } = get(timelineDatesAtom)

  return eachMonthOfInterval({
    start: timelineStartDate,
    end: timelineEndDate
  })
})

const allWeeksInTimelineAtom = atom<Date[]>(get => {
  const { timelineStartDate, timelineEndDate } = get(timelineDatesAtom)

  return eachWeekOfInterval({
    start: timelineStartDate,
    end: timelineEndDate
  })
})

export const timelineDayOffsetsAtom = atom<Record<string, number>>(get => {
  const allDaysInTimeline = get(allDaysInTimelineAtom)

  return allDaysInTimeline.reduce(
    (acc, date, idx) => {
      acc[format(date, TIMELINE_DATE_OFFSET_FORMAT)] = idx * DAY_RESOLUTION_PX
      return acc
    },
    {} as Record<string, number>
  )
})

export const timelineMonthsAtom = atom<
  Array<{
    date: Date
    offset: number
  }>
>(get => {
  const allMonthsInTimeline = get(allMonthsInTimelineAtom)
  const timelineDayOffsets = get(timelineDayOffsetsAtom)

  return allMonthsInTimeline.map(date => ({
    date,
    offset: timelineDayOffsets[format(date, TIMELINE_DATE_OFFSET_FORMAT)] ?? 0
  }))
})

export const timelineWeeksAtom = atom<
  Array<{
    date: Date
    offset: number
  }>
>(get => {
  const allWeeksInTimeline = get(allWeeksInTimelineAtom)
  const timelineDayOffsets = get(timelineDayOffsetsAtom)

  return allWeeksInTimeline.map(date => ({
    date,
    offset: timelineDayOffsets[format(date, TIMELINE_DATE_OFFSET_FORMAT)] ?? 0
  }))
})

export type ProductTimeline = {
  billingPeriods: Array<{
    start: Date
    end: Date
    startOffset: number
    endOffset: number
    inAdvance: boolean
    isOneTime: boolean
    proratedPrice?: {
      inAdvance: boolean
      startDateOffset: number
    }
  }>
  openEnded: {
    start?: Date
    startOffset?: number
  }
  prices: Array<{
    id: string
    summary: {
      price: string
      discountedPrice: string
      frequency: string
    }
    isOneTime: boolean
    inAdvance: boolean
    name: string
    startDate: {
      date: Date
      offset: number
    }
  }>
}

export const productTimelinesAtom = atom<Record<string, ProductTimeline>>(
  get => {
    const productPriceHistory = get(productPriceHistoryAtom)
    const timelineDayOffsets = get(timelineDayOffsetsAtom)

    return Object.entries(productPriceHistory).reduce(
      (acc, [productId, productTimeline]) => {
        const productPrices = productTimeline.prices.map(productPrice => ({
          ...productPrice,
          startDate: {
            date: productPrice.startDate,
            offset:
              timelineDayOffsets[
                format(productPrice.startDate, TIMELINE_DATE_OFFSET_FORMAT)
              ]
          }
        }))
        /** Useful for finding the latest applicable price for a billing period */
        const reversedProductPrices = [...productPrices].reverse()

        acc[productId] = {
          openEnded: {
            start: productTimeline.openEndedStartDate,
            startOffset:
              productTimeline.openEndedStartDate &&
              timelineDayOffsets[
                format(
                  productTimeline.openEndedStartDate,
                  TIMELINE_DATE_OFFSET_FORMAT
                )
              ]
          },
          prices: productPrices,
          billingPeriods: productTimeline.billingPeriods.map(billingPeriod => {
            const startOffset =
              timelineDayOffsets[
                format(billingPeriod.start, TIMELINE_DATE_OFFSET_FORMAT)
              ]
            const endOffset =
              timelineDayOffsets[
                format(billingPeriod.end, TIMELINE_DATE_OFFSET_FORMAT)
              ]
            const proratedPrice = productPrices.find(
              p =>
                p.startDate.offset > startOffset &&
                p.startDate.offset < endOffset
            )
            const price = reversedProductPrices.find(
              p => p.startDate.offset <= startOffset
            )

            return {
              ...billingPeriod,
              startOffset,
              endOffset,
              inAdvance: price?.inAdvance ?? false,
              isOneTime: price?.isOneTime ?? false,
              proratedPrice: proratedPrice && {
                inAdvance: proratedPrice?.inAdvance ?? false,
                startDateOffset: proratedPrice?.startDate.offset ?? 0
              }
            }
          })
        }

        return acc
      },
      {} as Record<string, ProductTimeline>
    )
  }
)

const getPhaseName = (phase: ProcessedPhase) => {
  if (phase.name) {
    return phase.name
  }

  if (phase.openEnded) {
    return `${format(phase.startDate as Date, TIMELINE_DATE_PHASE_FORMAT)} → Ongoing`
  }

  return `${format(phase.startDate as Date, TIMELINE_DATE_PHASE_FORMAT)} → ${format(phase.endDate as Date, TIMELINE_DATE_PHASE_FORMAT)}`
}

export type TimelinePhase = {
  startDate: {
    date: Date
    offset: number
  }
  id: string
  name: string
  resetsBilling: boolean
}

export const timelinePhasesAtom = atom<Array<TimelinePhase>>(get => {
  const sortedPhases = get(processedPhasesAtom)
  const timelineDayOffsets = get(timelineDayOffsetsAtom)

  return (
    sortedPhases
      .filter(phase => phase.startDate)
      .map(phase => {
        return {
          startDate: {
            date: phase.startDate,
            offset:
              timelineDayOffsets[
                format(phase.startDate, TIMELINE_DATE_OFFSET_FORMAT)
              ]
          },
          id: phase.id,
          name: getPhaseName(phase),
          resetsBilling: phase.recurrencePreference === 'RESET'
        }
      }) ?? []
  )
})

export type TimelineMinimum = {
  id: string
  summary: {
    amount: string
    frequency: string
  }
  productIds: Array<string>
  startDate: {
    date: Date
    offset: number
  }
  endDate: {
    date: Date
    offset: number
  }
  openEnded: boolean
}

export const timelineMinimumsAtom = atom<Array<TimelineMinimum>>(get => {
  const minimums = get(minimumsHistoryAtom)
  const timelineDayOffsets = get(timelineDayOffsetsAtom)

  return minimums.map(minimum => ({
    ...minimum,
    startDate: {
      date: minimum.startDate,
      offset:
        timelineDayOffsets[
          format(minimum.startDate, TIMELINE_DATE_OFFSET_FORMAT)
        ]
    },
    endDate: {
      date: minimum.endDate,
      offset:
        timelineDayOffsets[format(minimum.endDate, TIMELINE_DATE_OFFSET_FORMAT)]
    }
  }))
})

export const progressOffsetAtom = atom<number>(get => {
  const timelineDayOffsets = get(timelineDayOffsetsAtom)

  return timelineDayOffsets[format(new Date(), TIMELINE_DATE_OFFSET_FORMAT)]
})

export const allProductIdsAtom = atom<Array<string>>(get => {
  const productTimelines = get(productTimelinesAtom)

  return Object.keys(productTimelines)
})
